import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowDownUp, Info, Wallet, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/hooks/useWallet";
import { usePresaleManager } from "@/hooks/usePresaleManager";
import { usePancakeSwapV4Trading } from "@/hooks/usePancakeSwapV4Trading";
import { usePresaleDetails } from "@/hooks/usePresaleDetails";
import { usePresales } from "@/hooks/usePresales";
import { useRealTimeOdds } from "@/hooks/useRealTimeOdds";
import { ERROR_MESSAGES, formatErrorMessage, logError } from "@/lib/errorMessages";

interface TradeWidgetProps {
  market: {
    id: string;
    yesPrice: number;
    noPrice: number;
  };
  onOutcomeChange?: (outcome: "YES" | "NO") => void;
}

const TradeWidget = ({ market, onOutcomeChange }: TradeWidgetProps) => {
  const [amount, setAmount] = useState("");
  const [outcome, setOutcome] = useState<"YES" | "NO">("YES");
  const [isTrading, setIsTrading] = useState(false);
  const [tradeEstimate, setTradeEstimate] = useState<any>(null);
  const [poolExists, setPoolExists] = useState(false);
  const [presaleStatus, setPresaleStatus] = useState<{
    isGraduated: boolean
    poolsCreated: boolean
    canTrade: boolean
    message: string
  } | null>(null);
  const statusCheckedRef = useRef<string | null>(null);
  const [yesBalance, setYesBalance] = useState(0);
  const [noBalance, setNoBalance] = useState(0);
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [isApproved, setIsApproved] = useState(false);

  const { isConnected, connect, address } = useWallet();
  const { balance: usdcBalance, contracts } = usePresaleManager();

  // Get presale details for pool key
  const { presaleDetails: contractPresaleDetails } = usePresaleDetails(market.id);

  // Get real-time odds from pool positions
  const {
    yesPrice: liveYesPrice,
    noPrice: liveNoPrice,
    isLoading: oddsLoading,
    error: oddsError
  } = useRealTimeOdds(market.id);

  // Use live prices if available, fallback to market prices
  const currentYesPrice = liveYesPrice || market.yesPrice;
  const currentNoPrice = liveNoPrice || market.noPrice;

  const {
    getTradeEstimate,
    executeTrade,
    executeSell,
    getTokenBalance,
    checkPCSV4Pool,
    checkPresaleStatus,
    clearBalanceCache,
    isPending: isTradePending,
    isConfirming: isTradeConfirming,
    isConfirmed: isTradeConfirmed,
    error: pcsTradeError,
    hash,
  } = usePancakeSwapV4Trading(undefined, market.id);

  const { data: subgraphPresales } = usePresales();

  // Use subgraph data for resolved markets, contract data for others
  const subgraphPresale = subgraphPresales?.find(p => p.id === market.id);
  const presaleDetails = (subgraphPresale || contractPresaleDetails) as any;

  // Debug: Log which data source is being used
  useEffect(() => {
    if (presaleDetails) {
      console.log('📊 PresaleDetails source:', subgraphPresale ? 'SUBGRAPH' : 'CONTRACT', {
        id: presaleDetails.id,
        yesToken: presaleDetails.yesToken,
        noToken: presaleDetails.noToken,
        tokensCreated: presaleDetails.tokensCreated
      });
    }
  }, [presaleDetails, subgraphPresale]);

  // Notify parent when outcome changes
  useEffect(() => {
    onOutcomeChange?.(outcome);
  }, [outcome, onOutcomeChange]);

  // Check presale status when presale details change
  useEffect(() => {
    const checkStatus = async () => {
      if (presaleDetails?.id && statusCheckedRef.current !== presaleDetails.id) {
        try {
          console.log('🔍 Checking presale status for:', presaleDetails.id);
          statusCheckedRef.current = presaleDetails.id;
          const status = await checkPresaleStatus(presaleDetails.id);
          console.log('📊 Presale status result:', status);
          setPresaleStatus(status);
        } catch (error) {
          console.error('❌ Error checking presale status:', error);
          setPresaleStatus({
            isGraduated: false,
            poolsCreated: false,
            canTrade: false,
            message: 'Error checking presale status'
          });
        }
      }
    };

    checkStatus();
  }, [presaleDetails?.id, checkPresaleStatus]);

  // Check approval status on component mount
  useEffect(() => {
    const checkApprovalStatus = () => {
      if (typeof window !== 'undefined' && contracts) {
        const cacheKey = `${contracts.USDC}-${contracts.PRESALE_MANAGER}`
        const localStorageKey = `approval_${cacheKey}`
        const cachedApproval = localStorage.getItem(localStorageKey)

        if (cachedApproval) {
          const { approved, timestamp } = JSON.parse(cachedApproval)
          const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 heures

          if (Date.now() - timestamp < CACHE_DURATION && approved) {
            setIsApproved(true)
          }
        }
      }
    }

    checkApprovalStatus()
  }, [contracts])

  // Manual balance refresh function with enhanced debounce
  const refreshBalances = async () => {
    if (!presaleDetails || !address || !presaleDetails.tokensCreated) return;

    try {
      // Reduced debounce: attendre 500ms avant de faire les appels
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('🔍 Refreshing balances with token addresses:', {
        yesToken: presaleDetails.yesToken,
        noToken: presaleDetails.noToken,
        userAddress: address
      });

      // Check if tokens are still valid before making calls
      if (presaleDetails.yesToken && presaleDetails.yesToken !== '0x0000000000000000000000000000000000000000') {
        const yesBal = await getTokenBalance(presaleDetails.yesToken, address);
        console.log('✅ YES token balance:', yesBal, 'for token:', presaleDetails.yesToken);
        setYesBalance(yesBal);

        // Add small delay between calls
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      if (presaleDetails.noToken && presaleDetails.noToken !== '0x0000000000000000000000000000000000000000') {
        const noBal = await getTokenBalance(presaleDetails.noToken, address);
        console.log('✅ NO token balance:', noBal, 'for token:', presaleDetails.noToken);
        setNoBalance(noBal);
      }
    } catch (error) {
      console.warn('Failed to refresh balances:', error);
      // Set balances to 0 on error to avoid stale data
      setYesBalance(0);
      setNoBalance(0);
    }
  };

  // Get token balances (only if tokens are created) - Auto refresh enabled
  useEffect(() => {
    if (presaleDetails && presaleDetails.tokensCreated && address) {
      refreshBalances();
    } else {
      setYesBalance(0);
      setNoBalance(0);
    }
  }, [presaleDetails, address]);

  // Clear balance cache and refresh balances after successful trade
  useEffect(() => {
    if (isTradeConfirmed && hash && amount) {
      setTimeout(() => {
        if (address) {
          clearBalanceCache(address);
        }
        refreshBalances();
      }, 2000); // Wait 2 seconds for transaction to be confirmed
    }
  }, [isTradeConfirmed, hash, amount, refreshBalances, clearBalanceCache, address]);

  // Get trade estimate when amount or outcome changes (only if tokens exist)
  useEffect(() => {
    // Reduced debounce for better responsiveness
    const timeoutId = setTimeout(async () => {
      if (amount && parseFloat(amount) > 0 && presaleDetails && presaleDetails.tokensCreated) {
        const tokenAddress = outcome === "YES" ? presaleDetails.yesToken : presaleDetails.noToken;

        if (tokenAddress && tokenAddress !== '0x0000000000000000000000000000000000000000') {
          try {
            // Get trade estimate for UI display
            const estimate = await getTradeEstimate(
              contracts.USDC,
              tokenAddress,
              amount,
              0.5
            );

            if (estimate) {
              setTradeEstimate(estimate);
              setPoolExists(true);
            } else {
              console.log('No trade estimate available');
              setTradeEstimate(null);
              setPoolExists(false);
            }
          } catch (error: any) {
            console.error('Error getting trade estimate:', error);
            setTradeEstimate(null);
            setPoolExists(false);
          }
        }
      } else {
        setTradeEstimate(null);
        setPoolExists(false);
      }
    }, 500); // Reduced debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [amount, outcome, presaleDetails, getTradeEstimate, contracts.USDC]);

  const handleTrade = async (type: "buy" | "sell") => {
    if (!isConnected) {
      toast.error(ERROR_MESSAGES.WALLET.NOT_CONNECTED);
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error(ERROR_MESSAGES.GENERAL.INVALID_INPUT);
      return;
    }

    if (!presaleDetails || !presaleDetails.tokensCreated) {
      toast.error(ERROR_MESSAGES.TRADING.TOKENS_NOT_CREATED);
      return;
    }

    if (!tradeEstimate) {
      toast.error(ERROR_MESSAGES.TRADING.NO_LIQUIDITY);
      return;
    }

    const amountNum = parseFloat(amount);
    const tokenAddress = outcome === "YES" ? presaleDetails.yesToken : presaleDetails.noToken;

    if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
      toast.error(ERROR_MESSAGES.TRADING.INVALID_OUTCOME);
      return;
    }

    try {
      setIsTrading(true);
      setTradeError(null);

      if (type === "buy") {
        // Check USDC balance
        if (amountNum > usdcBalance) {
          toast.error(`${ERROR_MESSAGES.TRADING.INSUFFICIENT_USDC}. You have $${usdcBalance.toFixed(2)}`);
          setIsTrading(false);
          return;
        }

        // Execute trade via PresaleManager
        toast.info(`Buying ${outcome} tokens...`);

        await executeTrade(
          contracts.USDC,
          tokenAddress,
          amount,
          0.5
        );

        toast.success(`Successfully swapped $${amount} USDT for ${tradeEstimate.amountOutFormatted.toFixed(2)} ${outcome} tokens!`);

        // Refresh balances after successful trade
        setTimeout(() => {
          refreshBalances();
        }, 2000);
      } else {
        // For selling: reverse the token flow (sell YES/NO for USDC)
        const tokenBalance = outcome === "YES" ? yesBalance : noBalance;

        if (amountNum > tokenBalance) {
          toast.error(`${ERROR_MESSAGES.TRADING.INSUFFICIENT_TOKENS}. You have ${tokenBalance.toFixed(2)} ${outcome} tokens`);
          setIsTrading(false);
          return;
        }

        // Execute sell trade via PancakeSwap V4 (router)
        toast.info(`Selling ${amount} ${outcome} tokens for USDT via PancakeSwap V4...`);
        await executeSell(tokenAddress, amount, 0.5);

        toast.success(`Successfully sold ${amount} ${outcome} tokens for $${tradeEstimate.amountOutFormatted.toFixed(2)} USDT!`);

        // Refresh balances
        setTimeout(() => {
          refreshBalances();
        }, 2000);
      }
    } catch (error: any) {
      console.error("Transaction error:", error);
      setTradeError(error.message);
      const errorMessage = formatErrorMessage(error, ERROR_MESSAGES.TRADING.TRADE_FAILED);
      toast.error(errorMessage);
    } finally {
      setIsTrading(false);
    }
  };

  const price = outcome === "YES" ? currentYesPrice : currentNoPrice;

  // Use real estimate if available, otherwise use simple calculation
  const shares = tradeEstimate
    ? tradeEstimate.amountOutFormatted.toFixed(2)
    : (amount ? (parseFloat(amount) / price).toFixed(2) : "0.00");

  const minimumReceived = tradeEstimate
    ? tradeEstimate.minimumReceived.toFixed(2)
    : (amount ? ((parseFloat(amount) / price) * 0.995).toFixed(2) : "0.00");

  const priceImpact = tradeEstimate
    ? tradeEstimate.priceImpact.toFixed(2)
    : "0.00";

  // Check if market is ready for trading
  // Market is ready if tokens are created (for both Graduated and Resolved states)
  const isMarketReady = presaleDetails && (
    presaleDetails.tokensCreated &&
    presaleDetails.yesToken !== '0x0000000000000000000000000000000000000000' &&
    presaleDetails.noToken !== '0x0000000000000000000000000000000000000000' &&
    (presaleDetails.state === 'Graduated' || presaleDetails.state === 'Resolved') // Allow trading after graduation or resolution
  );

  // Check if presale exists but is not graduated yet
  const isPresalePending = presaleDetails && presaleDetails.state === 'Pending';

  return (
    <Card className="p-6 bg-card border-border sticky top-24">
      <h3 className="text-xl font-bold mb-4 text-foreground">Trade</h3>

      {/* Approval Info */}
      {isConnected && (
        <div className="p-3 rounded-lg mb-4 flex items-center gap-2 bg-blue-500/10 border border-blue-500/20">
          <Info className="w-5 h-5 text-blue-500" />
          <div>
            <p className="text-sm font-medium text-blue-500">Approval Info</p>
            <p className="text-xs text-blue-500/80">First trade will require USDT approval (~$0.01)</p>
          </div>
        </div>
      )}

      {/* Market Not Ready */}
      {!isMarketReady && presaleDetails && (
        <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg text-center">
          <AlertCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-orange-500 mb-1">
            {presaleDetails.state === 'Resolved' ? "Market Resolved" :
              presaleDetails.state === 'Pending' ? "Presale Not Graduated" :
                "Market Not Yet Active"}
          </p>
          <p className="text-xs text-orange-500/80">
            {presaleDetails.state === 'Active' ? "Presale is still in funding phase" :
              presaleDetails.state === 'Pending' ? "Pool not created yet - presale needs to be graduated first to create PancakeSwap V4 pools" :
                presaleDetails.state === 'Graduated' && !presaleDetails.tokensCreated ? "Tokens are being created" :
                  presaleDetails.state === 'Resolved' ? "Market resolved - trading via PancakeSwap V4 pools" :
                    "Market is not ready for trading"}
          </p>
        </div>
      )}

      {/* Wallet Status */}
      {!isConnected && isMarketReady ? (
        <div className="mb-4 p-4 bg-muted/50 rounded-lg border border-border text-center">
          <Wallet className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-3">Connect your wallet to trade</p>
          <Button onClick={connect} className="w-full">
            <Wallet className="w-4 h-4 mr-2" />
            Connect Wallet
          </Button>
        </div>
      ) : isMarketReady ? (
        <div className="mb-4 space-y-2">
          <div className="p-3 bg-muted/30 rounded-lg border border-border">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">USDT Balance</span>
              <span className="font-medium text-foreground">${usdcBalance.toFixed(2)}</span>
            </div>
          </div>
          {presaleDetails && presaleDetails.tokensCreated && (
            <div className="space-y-2">
              <span className="text-sm font-medium">Token Balances</span>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-success/5 rounded border border-success/20">
                  <div className="text-xs text-muted-foreground">YES Balance</div>
                  <div className="text-sm font-medium text-foreground">{yesBalance.toFixed(2)}</div>
                </div>
                <div className="p-2 bg-destructive/5 rounded border border-destructive/20">
                  <div className="text-xs text-muted-foreground">NO Balance</div>
                  <div className="text-sm font-medium text-foreground">{noBalance.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}



      {/* Transaction Status */}
      {isMarketReady && (isTradePending || isTradeConfirming || isTradeConfirmed || pcsTradeError) && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${isTradeConfirmed ? 'bg-success/10 border border-success/20' :
          pcsTradeError ? 'bg-destructive/10 border border-destructive/20' :
            'bg-primary/10 border border-primary/20'
          }`}>
          {isTradeConfirmed ? (
            <CheckCircle className="w-4 h-4 text-success" />
          ) : pcsTradeError ? (
            <AlertCircle className="w-4 h-4 text-destructive" />
          ) : (
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          )}
          <span className="text-sm">
            {isTradeConfirmed ? 'Trade completed successfully!' :
              pcsTradeError ? 'Trade failed' :
                isTradePending ? 'Executing trade...' :
                  isTradeConfirming ? 'Confirming transaction...' :
                    'Processing...'}
          </span>
        </div>
      )}

      {isMarketReady && isConnected && (
        <Tabs defaultValue="buy" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="buy">Buy</TabsTrigger>
            <TabsTrigger value="sell">Sell</TabsTrigger>
          </TabsList>

          <TabsContent value="buy" className="space-y-4">
            {/* Outcome Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Outcome</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={outcome === "YES" ? "default" : "outline"}
                  onClick={() => setOutcome("YES")}
                  className={outcome === "YES" ? "bg-success hover:bg-success/90" : ""}
                >
                  YES {(currentYesPrice).toFixed(0)}%
                  {liveYesPrice && (
                    <span className="text-xs text-green-600 ml-1">● Live</span>
                  )}
                </Button>
                <Button
                  variant={outcome === "NO" ? "default" : "outline"}
                  onClick={() => setOutcome("NO")}
                  className={outcome === "NO" ? "bg-destructive hover:bg-destructive/90" : ""}
                >
                  NO {(currentNoPrice).toFixed(0)}%
                  {liveNoPrice && (
                    <span className="text-xs text-green-600 ml-1">● Live</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Amount (USDT)</label>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-lg"
              />
            </div>

            {/* Trade Details - Real-time estimate from Uniswap */}
            {amount && parseFloat(amount) > 0 && (
              <div className="space-y-2 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">You receive (estimated)</span>
                  <span className="font-medium text-foreground">
                    {tradeEstimate ? (
                      <>{shares} {outcome} <span className="text-xs text-success">✓ Live</span></>
                    ) : (
                      <>{shares} {outcome} <span className="text-xs text-muted-foreground">~Calc</span></>
                    )}
                  </span>
                </div>
                {tradeEstimate && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Minimum received</span>
                      <span className="font-medium text-foreground">{minimumReceived} {outcome}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Price impact</span>
                      <span className={`font-medium ${parseFloat(priceImpact) > 5 ? 'text-destructive' :
                        parseFloat(priceImpact) > 2 ? 'text-orange-500' :
                          'text-foreground'
                        }`}>
                        {priceImpact}%
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Slippage tolerance</span>
                  <span className="font-medium text-foreground">0.5%</span>
                </div>
              </div>
            )}

            {/* Impact Warning */}
            {amount && parseFloat(amount) > 5000 && (
              <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <Info className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <p className="text-xs text-orange-500">
                  Large trade detected. Price impact may be significant.
                </p>
              </div>
            )}

            {/* Price Impact Warning */}
            {tradeEstimate && parseFloat(priceImpact) > 5 && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-destructive">High Price Impact!</p>
                  <p className="text-xs text-destructive/80">
                    This trade will significantly move the market price
                  </p>
                </div>
              </div>
            )}

            {/* Points Info */}
            <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-primary">
                {amount && parseFloat(amount) > 0 ? (
                  <>You'll earn <strong>{parseFloat(amount).toLocaleString()} points</strong></>
                ) : (
                  <>Earn 1 point per $1 traded</>
                )}
              </p>
            </div>


            {/* Action Button */}
            <Button
              onClick={() => handleTrade("buy")}
              className="w-full"
              size="lg"
              disabled={!isConnected || isTrading || isTradePending || isTradeConfirming || !amount || parseFloat(amount) <= 0 || !presaleStatus?.canTrade}
            >
              {isTrading || isTradePending || isTradeConfirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isTradePending ? "Approving..." : isTradeConfirming ? "Confirming..." : "Processing..."}
                </>
              ) : !presaleStatus?.canTrade && amount && parseFloat(amount) > 0 ? (
                presaleStatus?.message || "No PancakeSwap V4 Liquidity Available"
              ) : (
                `Buy ${outcome}`
              )}
            </Button>
          </TabsContent>

          <TabsContent value="sell" className="space-y-4">
            {/* Outcome Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Outcome</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={outcome === "YES" ? "default" : "outline"}
                  onClick={() => setOutcome("YES")}
                  className={outcome === "YES" ? "bg-success hover:bg-success/90" : ""}
                >
                  YES {(currentYesPrice).toFixed(0)}%
                  {liveYesPrice && (
                    <span className="text-xs text-green-600 ml-1">● Live</span>
                  )}
                </Button>
                <Button
                  variant={outcome === "NO" ? "default" : "outline"}
                  onClick={() => setOutcome("NO")}
                  className={outcome === "NO" ? "bg-destructive hover:bg-destructive/90" : ""}
                >
                  NO {(currentNoPrice).toFixed(0)}%
                  {liveNoPrice && (
                    <span className="text-xs text-green-600 ml-1">● Live</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Shares Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Shares to sell</label>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-lg"
              />
              <p className="text-xs text-muted-foreground">
                Available: {outcome === "YES" ? yesBalance.toFixed(2) : noBalance.toFixed(2)} {outcome} shares
              </p>
            </div>

            {/* Trade Details - Selling tokens for USDC */}
            {amount && parseFloat(amount) > 0 && (
              <div className="space-y-2 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">You receive (estimated)</span>
                  <span className="font-medium text-foreground">
                    {tradeEstimate ? (
                      <>${tradeEstimate.amountOutFormatted.toFixed(2)} USDT <span className="text-xs text-success">✓ Live</span></>
                    ) : (
                      <>${(parseFloat(amount) * price).toFixed(2)} USDT <span className="text-xs text-muted-foreground">~Calc</span></>
                    )}
                  </span>
                </div>
                {tradeEstimate && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Minimum received</span>
                      <span className="font-medium text-foreground">${tradeEstimate.minimumReceived.toFixed(2)} USDT</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Price impact</span>
                      <span className={`font-medium ${parseFloat(priceImpact) > 5 ? 'text-destructive' :
                        parseFloat(priceImpact) > 2 ? 'text-orange-500' :
                          'text-foreground'
                        }`}>
                        {priceImpact}%
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Slippage tolerance</span>
                  <span className="font-medium text-foreground">0.5%</span>
                </div>
              </div>
            )}

            {/* Points Info */}
            <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-primary">
                {amount && parseFloat(amount) > 0 ? (
                  <>You'll earn <strong>{(tradeEstimate ? tradeEstimate.amountOutFormatted : parseFloat(amount) * price).toLocaleString(undefined, { maximumFractionDigits: 0 })} points</strong></>
                ) : (
                  <>Earn 1 point per $1 traded</>
                )}
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => handleTrade("sell")}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              size="lg"
              disabled={!isConnected || isTrading || isTradePending || isTradeConfirming || !amount || parseFloat(amount) <= 0 || !presaleStatus?.canTrade}
            >
              {isTrading || isTradePending || isTradeConfirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isTradePending ? "Approving..." : isTradeConfirming ? "Confirming..." : "Processing..."}
                </>
              ) : !presaleStatus?.canTrade && amount && parseFloat(amount) > 0 ? (
                presaleStatus?.message || "No PancakeSwap V4 Liquidity Available"
              ) : (
                `Sell ${outcome}`
              )}
            </Button>
          </TabsContent>
        </Tabs>
      )}
    </Card>
  );
};

export default TradeWidget;
